import { CaretRightIcon, DotsThreeIcon } from "@phosphor-icons/react";
import { Fragment, type ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card/card";
import { Badge } from "@/components/ui/data-display/badge";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import IconButton from "@/components/ui/icon-button";
import {
  Menu,
  MenuPopover,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu";
import { CustomMenuItem, type MenuItemConfig } from "@/components/ui/menu-item";
import { cn } from "@/lib/utils";

const sectionCountLabel = (slotCount: number) =>
  `${slotCount} ${slotCount === 1 ? "section" : "sections"}`;

const briefingCountLabel = (briefingCount: number) =>
  `${briefingCount} ${briefingCount === 1 ? "briefing" : "briefings"}`;

const TemplateThumbnailCardDefaultPreview = () => (
  <div className="flex size-full justify-center bg-[#fefefe] px-4 pt-10.25">
    <div className="flex h-35 w-full max-w-67.5 min-w-0 flex-col gap-3 overflow-hidden rounded-t bg-white p-4 shadow-[0px_0px_20px_0px_rgba(0,0,0,0.08)]">
      <p className="text-[10px] font-medium tracking-[0.08em] text-gray-400 uppercase">
        Contents
      </p>
      <ul className="space-y-1 text-[10px] leading-4 text-gray-500">
        <li>01 Sample section</li>
        <li>02 Sample section</li>
        <li>03 Sample section</li>
      </ul>
      <div className="mt-auto space-y-1">
        <p className="text-[11px] font-medium text-gray-800">Sample section</p>
        <p className="text-[10px] leading-4 text-gray-500">
          Sample output. Live editions use the section prompts set on your
          briefing.
        </p>
      </div>
    </div>
  </div>
);

const TemplateThumbnailCardPreviewSkeleton = () => (
  <div className="flex size-full justify-center bg-[#fefefe] px-4 pt-10.25">
    <div className="flex h-35 w-full max-w-67.5 min-w-0 flex-col gap-3 overflow-hidden rounded-t bg-white p-4 shadow-[0px_0px_20px_0px_rgba(0,0,0,0.08)]">
      <Skeleton className="h-2.5 w-16" />
      <div className="space-y-1.5">
        <Skeleton className="h-2.5 w-full" />
        <Skeleton className="h-2.5 w-[92%]" />
        <Skeleton className="h-2.5 w-[85%]" />
      </div>
      <div className="mt-auto space-y-1.5">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-2.5 w-full" />
        <Skeleton className="h-2.5 w-4/5" />
      </div>
    </div>
  </div>
);

interface TemplateThumbnailCardBaseProps {
  className?: string;
}

export interface TemplateThumbnailCardLoadedProps
  extends TemplateThumbnailCardBaseProps {
  isLoading?: false;
  isInteractive?: boolean;
  /** Compact grey card: faded thumbnail, title and an Archived pill only. */
  isArchived?: boolean;
  name: string;
  description?: string;
  updatedLabel: string;
  slotCount: number;
  briefingCount: number;
  thumbnail?: ReactNode;
}

interface TemplateThumbnailCardLoadingProps
  extends TemplateThumbnailCardBaseProps {
  isLoading: true;
}

export type TemplateThumbnailCardProps =
  | TemplateThumbnailCardLoadedProps
  | TemplateThumbnailCardLoadingProps;

const cardClassName = (className?: string, isInteractive = false) =>
  cn(
    "flex h-full w-full flex-col gap-0 overflow-hidden rounded-xl border border-slate-200 bg-white py-0 shadow-[0px_0px_8px_0px_rgba(0,0,0,0.04)] ring-0",
    isInteractive &&
      // `group-hover/template-card` keeps the highlight while hovering a sibling overlay like the ⋯ menu.
      "transition-[border-color,box-shadow] duration-150 hover:border-violet-200 hover:shadow-[0px_0px_12px_0px_rgba(0,0,0,0.08)] group-hover/template-card:border-violet-200 group-hover/template-card:shadow-[0px_0px_12px_0px_rgba(0,0,0,0.08)]",
    className,
  );

const TemplateThumbnailCardLoading = ({
  className,
}: TemplateThumbnailCardLoadingProps) => (
  <Card
    aria-busy="true"
    aria-label="Loading template"
    className={cardClassName(className)}
  >
    <div className="relative h-45 overflow-hidden border-b border-gray-200 bg-white">
      <TemplateThumbnailCardPreviewSkeleton />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-18.25 bg-linear-to-b from-transparent to-gray-50/50"
      />
    </div>

    <CardContent className="flex flex-1 flex-col gap-3 px-3.75 pt-3 pb-4">
      <div className="h-16 shrink-0 space-y-1">
        <Skeleton className="h-5 w-3/5" />
        <Skeleton className="h-10 w-full" />
      </div>

      <div className="mt-auto flex min-h-8 shrink-0 flex-col gap-3">
        <div className="border-t border-slate-200" />

        <div className="flex min-h-4 items-center justify-between gap-2">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-4 w-28" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const TemplateThumbnailCard = (props: TemplateThumbnailCardProps) => {
  if (props.isLoading) {
    return <TemplateThumbnailCardLoading {...props} />;
  }

  const {
    name,
    description,
    updatedLabel,
    slotCount,
    briefingCount,
    thumbnail,
    className,
    isInteractive = false,
    isArchived = false,
  } = props;

  return (
    <Card className={cardClassName(className, isInteractive)}>
      <div
        className={cn(
          "relative h-[180px] overflow-hidden border-b border-gray-200 bg-white",
          isArchived && "opacity-60 blur-[2px]",
        )}
      >
        {thumbnail ? (
          <div className="size-full">{thumbnail}</div>
        ) : (
          <div
            role="img"
            aria-label={`Preview of ${name}`}
            className="size-full"
          >
            <TemplateThumbnailCardDefaultPreview />
          </div>
        )}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-18.25 bg-linear-to-b from-transparent to-gray-50/50"
        />
      </div>

      {isArchived ? (
        <CardContent className="flex items-center justify-between gap-2 px-3.75 py-3">
          <h2
            className="line-clamp-1 text-sm leading-5 font-normal text-gray-500"
            title={name}
          >
            {name}
          </h2>
          <Badge
            variant="muted"
            shape="pill"
            bordered={false}
            className="shrink-0"
          >
            Archived
          </Badge>
        </CardContent>
      ) : (
        <CardContent className="flex flex-1 flex-col gap-3 px-3.75 pt-3 pb-4">
          <div className="h-16 shrink-0 overflow-hidden">
            <h2
              className="line-clamp-1 h-5 text-sm leading-5 font-medium text-gray-900"
              title={name}
            >
              {name}
            </h2>
            <p
              className="mt-1 line-clamp-2 h-10 overflow-hidden text-sm leading-5 font-normal tracking-[0.14px] text-gray-500"
              title={description ?? undefined}
            >
              {description?.trim() ? description : "\u00a0"}
            </p>
          </div>

          <div className="mt-auto flex min-h-8 shrink-0 flex-col gap-3">
            <div className="border-t border-slate-200" />

            <div className="flex min-h-4 items-center justify-between gap-2 text-xs leading-4 tracking-[0.12px] text-gray-500">
              <div className="flex min-w-0 items-center gap-x-2 overflow-hidden">
                <span className="whitespace-nowrap">
                  {sectionCountLabel(slotCount)}
                </span>
                <span
                  aria-hidden
                  className="size-1 shrink-0 rounded-full bg-gray-400"
                />
                <span className="whitespace-nowrap">
                  {briefingCountLabel(briefingCount)}
                </span>
              </div>
              <span className="inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap">
                {updatedLabel}
                <CaretRightIcon aria-hidden className="size-3.5" />
              </span>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export interface TemplateThumbnailCardMenuProps {
  templateName: string;
  items: MenuItemConfig[];
  className?: string;
}

const templateCardMenuItemClassName =
  "gap-2 px-4 py-2 text-sm leading-5 tracking-[0.14px] text-gray-900";

const templateCardMenuPopoverClassName =
  "w-[222px] border-gray-200 shadow-[0px_20px_25px_-5px_rgba(18,18,23,0.1),0px_10px_10px_-5px_rgba(18,18,23,0.04)]";

/**
 * ⋯ menu for a template card. Render it as a sibling of the card's link or button,
 * inside a `group/template-card relative` container, so opening it doesn't activate the card.
 */
export const TemplateThumbnailCardMenu = ({
  templateName,
  items,
  className,
}: TemplateThumbnailCardMenuProps) => {
  return (
    <div
      className={cn(
        "absolute top-3 right-3 opacity-0 transition-opacity group-hover/template-card:opacity-100",
        // Keyboard focus on the card link or the trigger. Not `focus-within`: focus returns to the trigger on close and would pin the ⋯ visible.
        "group-has-[a:focus-visible]/template-card:opacity-100 has-[button:focus-visible]:opacity-100",
        "has-[button[aria-expanded=true]]:opacity-100",
        // Touch has no hover, so keep the ⋯ visible rather than an invisible tap target over the card link.
        "[@media(hover:none)]:opacity-100",
        className,
      )}
    >
      <MenuTrigger>
        <IconButton
          icon={<DotsThreeIcon size={20} />}
          variant="ghost"
          size="sm"
          aria-label={`More options for ${templateName}`}
          className="shrink-0 p-0.5 text-gray-600 data-hovered:bg-gray-100 data-pressed:bg-gray-100 [&_svg]:size-5 [&_svg]:shrink-0"
        />
        <MenuPopover
          placement="bottom end"
          offset={4}
          className={templateCardMenuPopoverClassName}
        >
          <Menu className="p-0 py-2">
            {items.map((item) => {
              const shouldRender = item.condition ?? true;
              if (!shouldRender) {
                return null;
              }

              return (
                <Fragment key={item.id}>
                  {item.separator ? (
                    <MenuSeparator className="mx-0 my-0 h-px bg-gray-200" />
                  ) : null}
                  <CustomMenuItem
                    config={{
                      ...item,
                      className: cn(
                        templateCardMenuItemClassName,
                        item.className,
                      ),
                    }}
                  />
                </Fragment>
              );
            })}
          </Menu>
        </MenuPopover>
      </MenuTrigger>
    </div>
  );
};
